// Admin panel — manage A/B copy tests for sub-service bullets.
// Workflow: pick a parent service + group_id + write the variant copy → save.
// The Landing page automatically rotates active variants per visitor session
// and tracks impressions/clicks/bookings via the existing sub_label pipeline.
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { FlaskConical, Loader2, Trophy, Trash2, Sparkles, Plus } from "lucide-react";

const SERVICES = [
  "lieferung", "escooter_service", "umzug", "reinigung", "hausreinigung",
  "gartenpflege", "autoteile", "regenrinne_reinigung", "winterdienst", "plakatservice",
];

export default function AdminABTests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [service, setService] = useState("gartenpflege");
  const [groupId, setGroupId] = useState("");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  // AI generator state
  const [aiBase, setAiBase] = useState("");
  const [aiLang, setAiLang] = useState("de");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/admin/ab-variants")
      .then((r) => setRows(r.data.variants || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!groupId.trim() || !label.trim()) return;
    setSaving(true);
    try {
      await api.post("/admin/ab-variants", { service, group_id: groupId.trim(), label: label.trim() });
      toast.success("Variant gespeichert!");
      setLabel("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Variant wirklich löschen?")) return;
    try { await api.delete(`/admin/ab-variants/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const markWinner = async (id) => {
    try { await api.post(`/admin/ab-variants/${id}/winner`); toast.success("Als Gewinner markiert"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const aiGenerate = async () => {
    if (!aiBase.trim()) { toast.error("Bitte Original-Bullet eingeben"); return; }
    setAiGenerating(true);
    setAiSuggestions([]);
    try {
      const r = await api.post("/admin/ab-variants/generate", {
        service, base_text: aiBase.trim(), count: 3, lang: aiLang,
      }, { timeout: 60000 });
      setAiSuggestions(r.data?.variants || []);
      if (!r.data?.variants?.length) toast.error("Keine Vorschläge erhalten");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI-Fehler");
    } finally {
      setAiGenerating(false);
    }
  };

  const adoptSuggestion = (text) => {
    setLabel(text);
    // Auto-derive a group_id from the AI base text (slug, first 30 chars)
    if (!groupId.trim()) {
      const slug = aiBase.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 30);
      if (slug) setGroupId(`${slug}_v1`);
    }
    toast.success("Übernommen — jetzt 'Speichern' klicken");
  };

  const autoSelect = async () => {
    try {
      const r = await api.post("/admin/ab-variants/auto-select");
      const n = r.data?.promoted_count || 0;
      if (n > 0) toast.success(`${n} Gewinner automatisch ausgewählt — E-Mail wurde versendet`);
      else toast.info("Noch keine eindeutigen Gewinner (≥100 Impressionen + ≥20% Vorsprung erforderlich)");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  // Group rows for display
  const byGroup = rows.reduce((acc, r) => {
    const k = `${r.service}::${r.group_id}`;
    acc[k] = acc[k] || { service: r.service, group_id: r.group_id, variants: [] };
    acc[k].variants.push(r);
    return acc;
  }, {});

  return (
    <div data-testid="admin-ab-tests">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h2 className="text-2xl font-black tracking-tight flex items-center gap-2">
          <FlaskConical size={18} className="text-[#FF5A00]" /> A/B Copy Tests
        </h2>
        <button
          type="button"
          onClick={autoSelect}
          className="inline-flex items-center gap-2 border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white font-bold uppercase tracking-wide text-xs px-4 py-2 transition"
          data-testid="ab-auto-select-btn"
          title="Sucht automatisch nach klaren Gewinnern (≥100 Impressionen + ≥20% Vorsprung) und sendet eine E-Mail"
        >
          <Trophy size={13} /> Auto-Auswahl
        </button>
      </div>

      <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-300 p-5 mb-6" data-testid="ai-variant-gen">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={14} className="text-purple-700" />
          <span className="text-[11px] font-bold tracking-widest uppercase text-purple-700">KI-Variant Generator</span>
        </div>
        <p className="text-xs text-neutral-600 mb-3 leading-relaxed">
          Gib einen Original-Bullet ein — Claude generiert 3 alternative Formulierungen mit unterschiedlichen Blickwinkeln.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <input
            type="text"
            value={aiBase}
            onChange={(e) => setAiBase(e.target.value)}
            placeholder="Original-Bullet (z.B. 'Klaviertransport & Spezialmöbel')"
            className="md:col-span-8 border border-neutral-300 px-3 py-2.5 text-sm bg-white"
            disabled={aiGenerating}
            data-testid="ai-base-input"
          />
          <select
            value={aiLang}
            onChange={(e) => setAiLang(e.target.value)}
            disabled={aiGenerating}
            className="md:col-span-2 border border-neutral-300 px-3 py-2.5 text-sm bg-white"
            data-testid="ai-lang-select"
          >
            <option value="de">DE</option>
            <option value="en">EN</option>
            <option value="ar">AR</option>
          </select>
          <button
            type="button"
            onClick={aiGenerate}
            disabled={aiGenerating || !aiBase.trim()}
            className="md:col-span-2 bg-purple-700 hover:bg-purple-800 text-white font-bold uppercase tracking-wide text-sm px-4 py-2.5 transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="ai-generate-btn"
          >
            {aiGenerating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Generieren
          </button>
        </div>
        {aiSuggestions.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="ai-suggestions">
            {aiSuggestions.map((s, i) => (
              <div key={i} className="flex items-start gap-3 bg-white border border-purple-200 px-3 py-2 hover:border-purple-500 transition group">
                <span className="text-[10px] font-bold tracking-widest uppercase text-purple-600 mt-1 flex-shrink-0">V{i + 1}</span>
                <span className="flex-1 text-sm text-neutral-800">{s}</span>
                <button
                  type="button"
                  onClick={() => adoptSuggestion(s)}
                  className="text-purple-700 hover:text-purple-900 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider flex-shrink-0"
                  data-testid={`ai-use-${i}`}
                >
                  <Plus size={12} /> Übernehmen
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-gradient-to-br from-orange-50 to-white border border-[#FF5A00]/30 p-5 mb-8">
        <div className="text-[11px] font-bold tracking-widest uppercase text-[#FF5A00] mb-2">Neue Variant</div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <select value={service} onChange={(e) => setService(e.target.value)} className="md:col-span-3 border border-neutral-300 px-3 py-2.5 text-sm bg-white" disabled={saving} data-testid="ab-service-select">
            {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input type="text" value={groupId} onChange={(e) => setGroupId(e.target.value)} placeholder="group_id (z.B. friedhof_v1)" className="md:col-span-3 border border-neutral-300 px-3 py-2.5 text-sm bg-white" disabled={saving} data-testid="ab-group-input" />
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Variant-Text (wird als Bullet angezeigt)" className="md:col-span-4 border border-neutral-300 px-3 py-2.5 text-sm bg-white" disabled={saving} data-testid="ab-label-input" />
          <button type="button" onClick={create} disabled={saving || !label.trim() || !groupId.trim()} className="md:col-span-2 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide text-sm px-4 py-2.5 transition disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="ab-create-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : "Speichern"}
          </button>
        </div>
        <p className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
          Tipp: Erstelle 2–3 Varianten pro group_id. Der Landing-Page zeigt jeder Besucher eine zufällige (stabil pro Session). Nach ≥100 Impressionen pro Variant markierst du den klaren Gewinner — die anderen werden automatisch ausgeblendet.
        </p>
      </div>

      {loading && <div className="text-sm text-neutral-500" data-testid="ab-loading">Laden…</div>}

      <div className="space-y-6" data-testid="ab-groups-list">
        {Object.values(byGroup).map((g) => (
          <div key={`${g.service}-${g.group_id}`} className="border border-neutral-200 bg-white">
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-neutral-50">
              <div>
                <div className="text-[10px] font-bold tracking-widest uppercase text-neutral-500">{g.service}</div>
                <div className="font-bold tracking-tight font-mono text-sm">{g.group_id}</div>
              </div>
              <span className="text-[11px] font-bold uppercase tracking-widest text-neutral-500">{g.variants.length} Variants</span>
            </div>
            <table className="w-full text-sm">
              <thead className="border-b border-neutral-200">
                <tr>
                  <Th>Variant</Th>
                  <Th className="text-end">Status</Th>
                  <Th className="text-end">Impr.</Th>
                  <Th className="text-end">Klicks</Th>
                  <Th className="text-end">CTR</Th>
                  <Th className="text-end">Buchungen</Th>
                  <Th className="text-end">Conv.</Th>
                  <Th className="text-end">Aktion</Th>
                </tr>
              </thead>
              <tbody>
                {g.variants.map((v) => {
                  const isWin = v.status === "winner";
                  const isLose = v.status === "loser";
                  return (
                    <tr key={v.variant_id} className={`border-b border-neutral-100 ${isWin ? "bg-emerald-50" : isLose ? "bg-neutral-50 opacity-60" : ""}`} data-testid={`ab-variant-${v.variant_id}`}>
                      <td className="px-4 py-3 text-neutral-800">{v.label}</td>
                      <td className="px-4 py-3 text-end">
                        <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          isWin ? "bg-emerald-100 text-emerald-800"
                          : isLose ? "bg-neutral-200 text-neutral-600"
                          : "bg-orange-100 text-[#E04F00]"
                        }`}>{v.status}</span>
                      </td>
                      <td className="px-4 py-3 text-end tabular-nums">{v.impressions || 0}</td>
                      <td className="px-4 py-3 text-end tabular-nums font-bold text-[#FF5A00]">{v.clicks || 0}</td>
                      <td className="px-4 py-3 text-end tabular-nums">{v.ctr_pct}%</td>
                      <td className="px-4 py-3 text-end tabular-nums font-bold text-emerald-700">{v.bookings || 0}</td>
                      <td className="px-4 py-3 text-end tabular-nums">{v.conversion_pct}%</td>
                      <td className="px-4 py-3 text-end whitespace-nowrap">
                        {!isWin && (
                          <button type="button" onClick={() => markWinner(v.variant_id)} className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider me-3" data-testid={`ab-winner-${v.variant_id}`}>
                            <Trophy size={12} /> Gewinner
                          </button>
                        )}
                        <button type="button" onClick={() => remove(v.variant_id)} className="text-red-600 hover:text-red-800" data-testid={`ab-delete-${v.variant_id}`}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
        {!loading && Object.keys(byGroup).length === 0 && (
          <div className="text-sm text-neutral-500 py-8 text-center" data-testid="ab-empty">Noch keine Varianten. Erstelle deine erste oben.</div>
        )}
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-2 text-[10px] font-bold tracking-widest uppercase text-neutral-500 text-start ${className}`}>{children}</th>
);
